import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/material";
import { Trans } from "@lingui/react/macro";
import { useLocation, useNavigate } from "react-router-dom";
import type { RootState } from "../../store/types.tsx";
import { useTypedSelector } from "../../hooks.tsx";
import { useFeatureFlag } from "../../hooks/useFeatureFlag.tsx";
import SelectLLMModel from "./SelectLLMModel.tsx";
import NotificationsDrawerAction from "../notifications/NotificationsDrawerAction.tsx";
import {
  addConversation,
  conversationActions,
  selectDashboardAction,
} from "../../store/conversation.tsx";
import ConversationSearch from "./ConversationSearch.tsx";
import DrawerNavRow from "./DrawerNavRow.tsx";
import {
  NewConversationIcon,
  DashboardsIcon,
  MemoriesIcon,
  IntegrationsIcon,
} from "../icons/DrawerNavIcons.tsx";
import { clearConversationUrl } from "./conversationUrl.ts";

interface ConversationDrawerActionsProps {
  closeDrawer: () => void;
  onShowIntegrations?: () => void;
  onShowMemories?: () => void;
  onConversationSelected?: () => void;
}

const ConversationDrawerActions: React.FC<ConversationDrawerActionsProps> = ({
  closeDrawer,
  onShowIntegrations,
  onShowMemories,
  onConversationSelected,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const availableModels = useSelector(
    (state: RootState) => state.conversationState.availableModels,
  );
  const hasDashboardsFeature = useFeatureFlag("dashboards");
  const dashboardIds = useTypedSelector(
    (state) => state.conversationState.dashboardIds,
  );
  const { selectedView, selectedDashboard } = useTypedSelector(
    (state) => state.conversationState,
  );

  // Which destination is on screen. The side pages are routes; notifications
  // and dashboards are views within the conversation panel.
  const isIntegrationsPage = location.pathname === "/integrations";
  const isMemoriesPage = location.pathname === "/memories";
  const isSidePage = isIntegrationsPage || isMemoriesPage;
  const isNotificationsView = !isSidePage && selectedView === "notifications";
  const isDashboardsView = !isSidePage && selectedView === "dashboard";

  const handleIntegrationsClick = () => {
    closeDrawer();
    onShowIntegrations?.();
  };

  const handleMemoriesClick = () => {
    closeDrawer();
    onShowMemories?.();
    navigate("/memories");
  };

  const handleDashboardsClick = () => {
    closeDrawer();
    // Leaves the integrations/memories routes, same as picking a conversation.
    onConversationSelected?.();
    dispatch(conversationActions.setSelectedView("dashboard"));
    // Open the most recent dashboard when none is selected yet, so the row
    // always lands somewhere.
    if (!selectedDashboard && dashboardIds.length > 0) {
      dispatch(selectDashboardAction(dashboardIds[0]) as any);
    }
  };

  const handleAddConversation = () => {
    closeDrawer();
    dispatch(conversationActions.setSelectedView("conversation"));
    dispatch(addConversation() as any);
    onConversationSelected?.();
    clearConversationUrl(navigate);
  };

  return (
    <Box
      marginBottom={2}
      sx={{
        py: "4px",
        backgroundColor: "inherit",
        flexShrink: 0,
        // The approved design stacks these rows with a small, even rhythm
        // rather than flush against one another.
        display: "flex",
        flexDirection: "column",
        gap: "3px",
      }}
    >
      <DrawerNavRow
        icon={<NewConversationIcon />}
        onClick={handleAddConversation}
        label={
          <Trans context="Drawer action label for creating a new conversation">
            New Conversation
          </Trans>
        }
      />

      <ConversationSearch
        closeDrawer={closeDrawer}
        onConversationSelected={onConversationSelected}
      />

      {/* Group break, matching the draft: composer actions above, destinations below. */}
      <Box sx={{ height: "8px", flexShrink: 0 }} />

      {hasDashboardsFeature && (
        <DrawerNavRow
          icon={<DashboardsIcon />}
          onClick={handleDashboardsClick}
          active={isDashboardsView}
          label={
            <Trans context="Drawer action label for dashboards">
              Dashboards
            </Trans>
          }
        />
      )}

      <DrawerNavRow
        icon={<IntegrationsIcon />}
        onClick={handleIntegrationsClick}
        active={isIntegrationsPage}
        label={
          <Trans context="Drawer action label for integrations page">
            Integrations
          </Trans>
        }
      />

      <DrawerNavRow
        icon={<MemoriesIcon />}
        onClick={handleMemoriesClick}
        active={isMemoriesPage}
        label={
          <Trans context="Drawer action label for memories page">
            Memories
          </Trans>
        }
      />

      <NotificationsDrawerAction
        closeDrawer={closeDrawer}
        onConversationSelected={onConversationSelected}
        active={isNotificationsView}
      />

      {availableModels && availableModels.length > 0 && (
        <SelectLLMModel availableModels={availableModels} />
      )}
    </Box>
  );
};

export default ConversationDrawerActions;
